import { FC } from 'react';
import classNames from 'classnames';
import {
  amountRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import styles from '../../opm-summary-waterfall-erf.module.scss';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters/number-value-formatter';
import { Tooltip, TooltipPlacement } from '@app/shared/components/tooltip/Tooltip';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import Button from '@app/shared/components/button/Button';
import SvgInformation from '@app/shared/icons/Information';
import { l } from '@locale/setupI18n';
import { NOT_APPLICABLE, WaterfallCellValue } from './sample-waterfall-column-helpers';
import WaterfallValueCell from './WaterfallValueCell';

interface TableRowProps {
  label: string;
  columnKeys: string[];
  valueSelector: (columnKey: string) => WaterfallCellValue;
  rowTestId: string;
  className?: string;
  percentage?: boolean;
  upper?: boolean;
  trancheIndentation?: boolean;
  hideRowIfEmpty?: boolean;
  canHavePlusSign?: boolean;
  canHaveTopLine?: boolean;
  cellClickFunction?: ((columnKey: string) => void) | undefined | null;
  toolTipContent?: string | null;
  bold?: boolean;
}

const allValuesAreEmpty = (
  columnKeys: string[],
  getValue: (columnKey: string) => WaterfallCellValue
) => {
  return columnKeys.every((key) => {
    const value = getValue(key);
    return value === NOT_APPLICABLE || Boolean(!value);
  });
};

const clickableCellToolTip = () => (
  <Tooltip
    content={l('_IrrNumbersCanBeClickedForFurtherInformation')}
    placement={TooltipPlacement.Right}
    useFixedPositioning>
    <Button
      appearance={ButtonAppearance.CLEAN}
      size={ButtonSize.FLUID}
      aria-label={l('_IrrNumbersCanBeClickedForFurtherInformation')}>
      <SvgInformation className={styles['warning-icon']} />
    </Button>
  </Tooltip>
);

const OrdinaryEquityRow: FC<TableRowProps> = ({
  label,
  columnKeys,
  valueSelector,
  className,
  rowTestId,
  percentage = false,
  upper = false,
  trancheIndentation = false,
  hideRowIfEmpty = false,
  canHavePlusSign = false,
  canHaveTopLine = false,
  cellClickFunction = null,
  toolTipContent = null,
  bold = false,
}: TableRowProps) => {
  if (allValuesAreEmpty(columnKeys, valueSelector) && hideRowIfEmpty) {
    return null;
  }

  return (
    <tr className={classNames(styles['border-right'], className)} data-testid={rowTestId}>
      <td
        className={classNames(styles['border-right'], {
          [styles['tranche-indentation']]: trancheIndentation,
          [styles['instrument-indentation']]: !trancheIndentation,
          [styles['bold-text']]: bold,
        })}>
        {/* the tooltip layout has to live inside the cell so that the cell keeps its
            table-cell display and the column borders stay aligned */}
        <div
          className={classNames({
            [styles['tooltip-header']]: Boolean(toolTipContent) || Boolean(cellClickFunction),
          })}>
          {label}
          {toolTipContent && (
            <Tooltip content={toolTipContent} placement={TooltipPlacement.Right}>
              <Button
                appearance={ButtonAppearance.CLEAN}
                size={ButtonSize.FLUID}
                aria-label={toolTipContent}>
                <SvgInformation className={styles['warning-icon']} />
              </Button>
            </Tooltip>
          )}
          {cellClickFunction && clickableCellToolTip()}
        </div>
      </td>
      {columnKeys.map((key) => {
        const value = valueSelector(key);

        return (
          <WaterfallValueCell
            key={key}
            value={
              percentage && typeof value === 'number'
                ? getCalculatedProjectValueInPercent(value)
                : value
            }
            className={classNames({
              [styles['clickable-cell']]: cellClickFunction,
            })}
            valueClassName={classNames({
              [styles['upper-text']]: upper && value !== false,
              [styles['upper-text-value-false']]: upper && value === false,
            })}
            rules={percentage ? enteredPercentageViewRules : amountRules}
            plusSign={canHavePlusSign && toolTipContent !== null}
            topLine={canHaveTopLine && toolTipContent !== null}
            onClick={cellClickFunction ? () => cellClickFunction(key) : undefined}
          />
        );
      })}
    </tr>
  );
};

export default OrdinaryEquityRow;
